// packages/contracts/src/demonstration-mode.ts

/**
 * Whether this gateway is running as a live DEMONSTRATION of the production
 * build — for an Authority audience, over the real code path, writing to a
 * real database, with no record reaching a real counterparty.
 *
 * ⚠ A CAPABILITY, not a deployment name — the same distinction
 * {@link IdentityProvider} and `SettlementDispatch` exist for, applied a third
 * time. PRS2.0 must be demonstrated to the Authority before the tender is won,
 * and what is demonstrated has to be the production build: a demo running on a
 * different artefact proves nothing about the one that ships. So demonstration
 * is a first-class, permanent capability of the production gateway — off by
 * default, enabled only by the exact string `'on'` in `PRS2_DEMONSTRATION_MODE`,
 * and loudly self-identifying on every response while it is active.
 *
 * - `'off'` — the ordinary, undecorated production build. This is every value
 *   `PRS2_DEMONSTRATION_MODE` can carry except the one exact string below:
 *   absent, empty, `'true'`, `'1'`, `'ON'`, and every typo. S-016 exists because
 *   a safety-adjacent claim was once derived from an environment string and
 *   printed itself for `PRODUCTION`, `prod` and every misspelling; this flag is
 *   deliberately the opposite shape — an allow-list of exactly one string, so a
 *   typo can only ever land on `'off'`, never on `'on'`.
 * - `'on'` — the gateway was booted with `PRS2_DEMONSTRATION_MODE=on`, and
 *   `assertDemonstrationModeSafe` (`services/gateway/src/demonstration-mode.ts`)
 *   has already refused to let that boot complete beside anything that could
 *   reach a real counterparty: a non-local outbound channel, or a
 *   `settlementDispatch` other than `'none'`. A client seeing `'on'` may
 *   therefore treat "nothing here reaches a real counterparty" as true for the
 *   same reason it may treat `identityProvider`/`settlementDispatch` as true —
 *   the server refused to boot into a state where it would be false.
 *
 * ⚠ **Never derived from `APP_ENV`, `NODE_ENV`, or any deployment name.** A
 * demonstration is not "an environment called demo" — it is this exact
 * production artefact, and the flag that says so must not drift with whatever
 * a deployment happens to be named. See `services/gateway/src/server.ts`'s
 * `identityProvider`/`settlementDispatch` comments for the incident this
 * mirrors, one capability over.
 *
 * ⚠ **This is not a safety claim on its own — it is a REPORT of a safety claim
 * the server already enforced at boot.** The client does not decide whether
 * demonstration mode is safe; it only decides whether to say so, on the
 * strength of the server having refused to start otherwise.
 */
export type DemonstrationMode = 'on' | 'off'

/** Every value {@link DemonstrationMode} admits, for narrowing at a boundary. */
export const DEMONSTRATION_MODES: readonly DemonstrationMode[] = ['on', 'off']

/**
 * Narrow an unknown value — a parsed JSON body, typically — to a
 * {@link DemonstrationMode}.
 *
 * Returns `false` for anything unrecognised rather than defaulting to one, the
 * same discipline {@link isIdentityProvider}-shaped guards in this package
 * follow: a client that folded an unrecognised value into `'off'` would be
 * making the same kind of judgement call the server itself refuses to make
 * about `PRS2_DEMONSTRATION_MODE`.
 */
export function isDemonstrationMode(value: unknown): value is DemonstrationMode {
  return typeof value === 'string' && (DEMONSTRATION_MODES as readonly string[]).includes(value)
}

/**
 * What ONE response actually said about `demonstrationMode`.
 *
 * ⚠ Identical in shape to `IdentityProviderReport`/`SettlementDispatchReport`,
 * for the reason S-017 records rather than for symmetry: a field the server did
 * not send arrives as `undefined` however confidently a client's `interface`
 * declares it a `string`, because a JSON body reaches that interface by a cast.
 * The EXISTENCE of a report is the fact that a response arrived; `reported:
 * null` is "it answered and did not say" — which, for this field, must never be
 * read as licence to show the demonstration banner, only as a reason not to.
 */
export interface DemonstrationModeReport {
  readonly reported: string | null
}

/**
 * Read a response's `demonstrationMode` into a {@link DemonstrationModeReport}.
 *
 * ⚠ No trimming, no case folding, no normalisation — `'  '` is a value the
 * server sent, matches nothing, and must surface as unrecognised rather than be
 * tidied into a match. Only a genuinely empty string is "said nothing".
 */
export function readDemonstrationMode(value: unknown): DemonstrationModeReport {
  return { reported: typeof value === 'string' && value.length > 0 ? value : null }
}
